import React, { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useModal } from '../../hooks/useModal';
import { useInterviewManagement } from '../../hooks/useInterviewManagement';

import EmployerHeaderNav from '../../components/navigation/EmployerHeaderNav';
import InterviewErrorBoundary from '../../components/interview/InterviewErrorBoundary';
import InterviewManagementHeader from '../../components/interview/InterviewManagementHeader';
import InterviewManagementSidebar from '../../components/interview/InterviewManagementSidebar';
import InterviewManagementContent from '../../components/interview/InterviewManagementContent';
import InterviewModalManager from '../../components/interview/InterviewModalManager';

import ErrorHandler from '../../utils/errorHandler';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../../constants/interviewConstants';

/**
 * Optimized Interview Management Page
 * Enhanced with performance optimizations, better error handling, security, and calendar integration
 */
const InterviewManagementPage = () => {
  const { user, userProfile } = useAuth();
  const { infoModal, showInfo, closeInfo } = useModal();
  const [searchParams] = useSearchParams();

  // Use the optimized interview management hook
  const {
    // State
    filters,
    activeTab,
    viewMode,
    modalState,
    calendarState,
    selectedItems,
    
    // Data
    interviews,
    allInterviews,
    loading,
    error,
    stats,
    tabs,
    calendarEvents,
    hasActiveFilters,
    
    // Operations
    operations,
    refresh,
    
    // Handlers
    setActiveTab,
    setViewMode,
    handleFilterChange,
    handleClearFilters,
    openModal,
    closeModal,
    handleScheduleInterview,
    handleReschedule,
    handleCancel,
    handleComplete,
    handleMessageCandidate,
    handleStatusChange,
    handleCalendarToggle,
    handleCalendarDateClick,
    handleCalendarInterviewClick
  } = useInterviewManagement(user?.id, userProfile?.role);

  // Handle modal query parameter
  useEffect(() => {
    const modalParam = searchParams.get('modal');
    if (modalParam === 'timeSlotManager') {
      openModal('timeSlotManager');
    }
  }, [searchParams, openModal]);

  // Enhanced handlers with error handling and user feedback
  const handleScheduleSubmit = useCallback(async (interviewData) => {
    try {
      let result;
      
      if (selectedItems.interview) {
        // Rescheduling existing interview
        const updateData = {
          ...interviewData,
          // Always set status to 'scheduled' when rescheduling
          status: 'scheduled',
          updated_at: new Date().toISOString()
        };

        // If there's a selectedApplication, include candidate and application IDs
        if (interviewData.selectedApplication) {
          updateData.candidate_id = interviewData.selectedApplication.applicant_id;
          updateData.application_id = interviewData.selectedApplication.id;
          // Note: job_id is not stored in interview_schedules, it's retrieved through application_id
        }

        result = await operations.updateInterview(
          selectedItems.interview.id,
          updateData
        );
        
        if (result.success) {
          showInfo({
            title: 'Interview Rescheduled',
            message: SUCCESS_MESSAGES.INTERVIEW_RESCHEDULED
          });
        }
      } else if (selectedItems.candidate) {
        // Scheduling new interview
        const candidate = selectedItems.candidate;
        
        if (interviewData.selectedApplication) {
          const applicationData = interviewData.selectedApplication;
          
          result = await operations.createInterview({
            ...interviewData,
            candidate_id: applicationData.applicant_id,
            job_id: applicationData.job_id,
            application_id: applicationData.id,
            interviewer_id: user.id,
            status: 'scheduled',
            created_at: new Date().toISOString()
          });
          
          if (result.success) {
            showInfo({
              title: 'Interview Scheduled',
              message: SUCCESS_MESSAGES.INTERVIEW_SCHEDULED
            });
          }
        } else {
          // Handle manual candidate creation if needed
          result = await operations.createInterview({
            ...interviewData,
            candidate_id: candidate.id || await createCandidateProfile(candidate),
            interviewer_id: user.id,
            status: 'scheduled',
            created_at: new Date().toISOString()
          });
          
          if (result.success) {
            showInfo({
              title: 'Interview Scheduled',
              message: SUCCESS_MESSAGES.INTERVIEW_SCHEDULED
            });
          }
        }
      }
      
      if (!result || !result.success) {
        throw new Error(result?.error || 'Failed to save interview');
      }
      
      closeModal('schedule');
      refresh();
    } catch (err) {
      ErrorHandler.logError('InterviewManagementPage:handleScheduleSubmit', err);
      showInfo({
        title: 'Error',
        message: err.message || ERROR_MESSAGES.GENERIC_ERROR
      });
    }
  }, [selectedItems, operations, user.id, showInfo, closeModal, refresh]);

  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    if (!selectedItems.interview) return;
    
    try {
      const completionResult = await operations.completeInterview(
        selectedItems.interview.id, 
        {
          ...feedbackData,
          interviewer_id: user.id,
          created_at: new Date().toISOString()
        }
      );
      
      if (!completionResult.success) {
        throw new Error(completionResult.error || 'Failed to complete interview');
      }
      
      showInfo({
        title: 'Interview Completed',
        message: SUCCESS_MESSAGES.INTERVIEW_COMPLETED
      });
      
      closeModal('feedback');
      refresh();
    } catch (err) {
      ErrorHandler.logError('InterviewManagementPage:handleFeedbackSubmit', err);
      showInfo({
        title: 'Error',
        message: err.message || ERROR_MESSAGES.GENERIC_ERROR
      });
    }
  }, [selectedItems.interview, operations, user.id, showInfo, closeModal, refresh]);

  const handleDecisionSubmit = useCallback(async (decisionData) => {
    if (!selectedItems.pendingStatusChange) return;
    
    try {
      const { interview, newStatus } = selectedItems.pendingStatusChange;
      
      const statusResult = await operations.updateHiringStatus(
        interview.id,
        newStatus,
        decisionData
      );
      
      if (!statusResult.success) {
        throw new Error(statusResult.error || 'Failed to update hiring status');
      }
      
      showInfo({
        title: 'Hiring Status Updated',
        message: SUCCESS_MESSAGES.HIRING_STATUS_UPDATED
      });
      
      closeModal('decision');
      refresh();
    } catch (err) {
      ErrorHandler.logError('InterviewManagementPage:handleDecisionSubmit', err);
      showInfo({
        title: 'Error',
        message: err.message || ERROR_MESSAGES.GENERIC_ERROR
      });
    }
  }, [selectedItems.pendingStatusChange, operations, showInfo, closeModal, refresh]);

  // Enhanced error handlers with user feedback
  const handleInterviewAction = useCallback(async (action, ...args) => {
    try {
      const result = await action(...args);
      if (result?.message) {
        showInfo({
          title: result.success ? 'Success' : 'Error',
          message: result.message
        });
      }
      return result;
    } catch (err) {
      ErrorHandler.logError('InterviewManagementPage:handleInterviewAction', err);
      showInfo({
        title: 'Error',
        message: err.message || ERROR_MESSAGES.GENERIC_ERROR
      });
      return { success: false, error: err.message };
    }
  }, [showInfo]);

  // Helper function for creating candidate profiles
  const createCandidateProfile = useCallback(async (candidate) => {
    const { data: newCandidate, error } = await supabase
      .from('user_profiles')
      .insert([
        {
          full_name: candidate.name,
          email: candidate.email,
          role: 'va',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])
      .select()
      .single();
      
    if (error) throw error;
    return newCandidate.id;
  }, []);

  return (
    <InterviewErrorBoundary fallbackUrl="/employer-dashboard">
      <div className="min-h-screen bg-background">
        <EmployerHeaderNav />
        
        <main className="pt-16 pb-20 md:pb-8">
          <div className="content-container flex flex-col gap-6">
            {/* Header */}
            <InterviewManagementHeader
              calendarView={calendarState.view}
              onCalendarToggle={handleCalendarToggle}
              onScheduleInterview={() => handleScheduleInterview({})}
              onOpenTimeSlotManager={() => openModal('timeSlotManager')}
            />

            {/* Calendar View - Full Width */}
            {calendarState.view && (
              <InterviewManagementContent
                calendarView={calendarState.view}
                calendarEvents={calendarEvents}
                onCalendarInterviewClick={handleCalendarInterviewClick}
                onCalendarDateClick={handleCalendarDateClick}
                interviews={interviews}
                loading={loading}
                error={error}
                activeTab={activeTab}
                viewMode={viewMode}
                hasActiveFilters={hasActiveFilters}
                onScheduleInterview={() => handleScheduleInterview({})}
                onReschedule={(id) => handleInterviewAction(handleReschedule, id)}
                onCancel={(id) => handleInterviewAction(handleCancel, id)}
                onComplete={(id) => handleInterviewAction(handleComplete, id)}
                onMessageCandidate={(interview) => handleInterviewAction(handleMessageCandidate, interview)}
                onStatusChange={(id, status) => handleInterviewAction(handleStatusChange, id, status)}
                onRetry={refresh}
              />
            )}
          </div>

          {!calendarState.view && (
            <div className="content-container flex flex-col-reverse md:flex-row gap-6">
              {/* Sidebar - Tabs and Filters */}
              <InterviewManagementSidebar
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
              
              {/* Main Content Area */}
              <div className="flex-grow">
                <InterviewManagementContent
                  calendarView={false}
                  interviews={interviews}
                  loading={loading}
                  error={error}
                  activeTab={activeTab}
                  viewMode={viewMode}
                  hasActiveFilters={hasActiveFilters}
                  onScheduleInterview={() => handleScheduleInterview({})}
                  onReschedule={(id) => handleInterviewAction(handleReschedule, id)}
                  onCancel={(id) => handleInterviewAction(handleCancel, id)}
                  onComplete={(id) => handleInterviewAction(handleComplete, id)}
                  onMessageCandidate={(interview) => handleInterviewAction(handleMessageCandidate, interview)}
                  onStatusChange={(id, status) => handleInterviewAction(handleStatusChange, id, status)}
                  onRetry={refresh}
                />
              </div>
            </div>
          )}
        </main>

        {/* Modal Manager */}
        <InterviewModalManager
          modalState={modalState}
          selectedItems={selectedItems}
          currentUserId={user?.id}
          infoModal={infoModal}
          onCloseModal={closeModal}
          onCloseInfo={closeInfo}
          onScheduleSubmit={handleScheduleSubmit}
          onFeedbackSubmit={handleFeedbackSubmit}
          onDecisionSubmit={handleDecisionSubmit}
          onReschedule={(id) => handleInterviewAction(handleReschedule, id)}
          onCancel={(id) => handleInterviewAction(handleCancel, id)}
          onComplete={(id) => handleInterviewAction(handleComplete, id)}
          onMessageCandidate={(interview) => handleInterviewAction(handleMessageCandidate, interview)}
          onOpenTimeSlotManager={() => openModal('timeSlotManager')}
        />
      </div>
    </InterviewErrorBoundary>
  );
};
export default InterviewManagementPage;